import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { TimelineEntry } from "./api.js";
import { MicIcon } from "./icons.js";

type Status = "idle" | "testing" | "recording" | "uploading" | "transcribing" | "error";
type TestPhase = "listening" | "checking" | "ready" | "failed";

interface RecordingContextValue {
  status: Status;
  /** Incrémenté quand les données changent (transcription, suppression) — pour rafraîchir les vues. */
  dataVersion: number;
  refreshData: () => void;
  elapsedMs: number;
  devices: MediaDeviceInfo[];
  deviceId: string | null;
  setDeviceId: (id: string | null) => void;
  changeMic: (id: string) => Promise<void>;
  analyser: AnalyserNode | null;
  micHeard: string | null;
  testPhase: TestPhase;
  heardSpeech: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  continueRecording: () => Promise<void>;
  retryTest: () => void;
  cancelTest: () => void;
  noteView: (eventId: string | null, photoId?: string | null) => void;
  error: string | null;
}

const Ctx = createContext<RecordingContextValue | null>(null);

const SPEECH_THRESHOLD = 0.045; // niveau RMS au-dessus duquel on considère qu'on parle
const CAPTURE_AFTER_SPEECH_MS = 1600; // durée capturée après le début de parole
const NO_SPEECH_TIMEOUT_MS = 9000; // délai avant d'abandonner le test

function pickMime(): string {
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

const SEG_SILENCE_LEVEL = 0.025; // en-dessous = silence
const SEG_SILENCE_MS = 800; // pause pour couper un segment
const SEG_MIN_MS = 1500; // durée min d'un segment
const SEG_MAX_MS = 25000; // durée max (coupe forcée si aucune pause)

/** Upload + transcription d'un segment (silence-borné) avec quelques tentatives. */
async function uploadSegment(
  id: string,
  seq: number,
  startMs: number,
  eventId: string | null,
  photoId: string | null,
  blob: Blob,
  contentType: string,
): Promise<void> {
  const qs = `seq=${seq}&startMs=${startMs}&eventId=${encodeURIComponent(eventId ?? "")}&photoId=${encodeURIComponent(photoId ?? "")}`;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const r = await fetch(`/api/recordings/${id}/segment?${qs}`, {
        method: "POST",
        headers: { "content-type": contentType },
        body: blob,
      });
      if (r.ok) return;
    } catch {
      /* réseau : on réessaie */
    }
    await new Promise((res) => setTimeout(res, 500 * (attempt + 1)));
  }
}

function computeLevel(analyser: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i]! - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}

/**
 * Écrêtage : proportion d'échantillons collés aux extrêmes. Un signal saturé est
 * distordu, et la transcription s'effondre bien avant que ce soit audible à l'oreille
 * — d'où l'intérêt de le montrer pendant le test micro plutôt que de le découvrir
 * dans le texte produit.
 */
function computeClipping(analyser: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(buf);
  let n = 0;
  for (let i = 0; i < buf.length; i++) {
    const b = buf[i]!;
    if (b <= 1 || b >= 254) n++;
  }
  return n / buf.length;
}

export function RecordingProvider({ children }: { children: ReactNode }) {
  const [status, setStatusState] = useState<Status>("idle");
  const [dataVersion, setDataVersion] = useState(0);
  const refreshData = useCallback(() => setDataVersion((v) => v + 1), []);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceIdState] = useState<string | null>(null);
  const deviceIdRef = useRef<string | null>(null);
  const setDeviceId = useCallback((id: string | null) => {
    deviceIdRef.current = id;
    setDeviceIdState(id);
  }, []);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [micHeard, setMicHeard] = useState<string | null>(null);
  const [testPhase, setTestPhase] = useState<TestPhase>("listening");
  const [heardSpeech, setHeardSpeech] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const analyserNodeRef = useRef<AnalyserNode | null>(null);

  const statusRef = useRef<Status>("idle");
  const setStatus = useCallback((s: Status) => {
    statusRef.current = s;
    setStatusState(s);
  }, []);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const testRecorderRef = useRef<MediaRecorder | null>(null);
  const startTsRef = useRef(0);
  const timelineRef = useRef<TimelineEntry[]>([]);
  const currentEventRef = useRef<string | null>(null);
  const currentPhotoRef = useRef<string | null>(null);
  const recordingIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const monitorRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seqRef = useRef(0);
  const pendingUploadsRef = useRef<Promise<void>[]>([]);
  const detectRafRef = useRef(0);
  // Segmentation aux silences.
  const segRecorderRef = useRef<MediaRecorder | null>(null);
  const segChunksRef = useRef<Blob[]>([]);
  const segStartMsRef = useRef(0);
  const segHasSpeechRef = useRef(false);
  const segLastSpeechRef = useRef(0);
  const cuttingRef = useRef(false);
  const cutSegmentRef = useRef<((final: boolean) => Promise<void>) | null>(null);

  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", refreshDevices);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", refreshDevices);
  }, [refreshDevices]);

  const noteView = useCallback((eventId: string | null, photoId: string | null = null) => {
    currentEventRef.current = eventId;
    currentPhotoRef.current = photoId;
    if (statusRef.current === "recording") {
      const tl = timelineRef.current;
      const last = tl[tl.length - 1];
      if (!last || last.eventId !== eventId || (last.photoId ?? null) !== photoId) {
        tl.push({ tMs: Math.round(performance.now() - startTsRef.current), eventId, photoId });
      }
    }
  }, []);

  const teardownStream = useCallback(() => {
    cancelAnimationFrame(detectRafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    analyserNodeRef.current = null;
    setAnalyser(null);
  }, []);

  // Sync périodique de la timeline vers le cloud (attribution crash-safe).
  const syncTimeline = useCallback(async (id: string) => {
    try {
      await fetch(`/api/recordings/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          timeline: timelineRef.current,
          durationMs: Math.round(performance.now() - startTsRef.current),
        }),
      });
    } catch {
      /* best-effort */
    }
  }, []);

  // Démarre le vrai enregistrement (upload par morceaux, crash-safe).
  const beginRecording = useCallback(async () => {
    const stream = streamRef.current;
    const an = analyserNodeRef.current;
    if (!stream || !an) return;

    const res = await fetch("/api/recordings", { method: "POST" });
    const { id } = (await res.json()) as { id: string };
    recordingIdRef.current = id;
    seqRef.current = 0;
    pendingUploadsRef.current = [];
    cuttingRef.current = false;

    const mimeType = pickMime();
    const uploadType = (mimeType || "audio/webm").split(";")[0]!;
    const buf = new Uint8Array(new ArrayBuffer(an.fftSize));

    startTsRef.current = performance.now();
    timelineRef.current = [{ tMs: 0, eventId: currentEventRef.current, photoId: currentPhotoRef.current }];

    const startSegment = () => {
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      segRecorderRef.current = rec;
      segChunksRef.current = chunks;
      segStartMsRef.current = Math.round(performance.now() - startTsRef.current);
      segHasSpeechRef.current = false;
      segLastSpeechRef.current = performance.now();
      rec.start();
    };

    // Coupe le segment courant, l'upload+transcrit, et (sauf final) en démarre un nouveau.
    const cutSegment = async (final: boolean) => {
      const rec = segRecorderRef.current;
      if (!rec) return;
      const seq = seqRef.current++;
      const startMs = segStartMsRef.current;
      const eventId = currentEventRef.current;
      const photoId = currentPhotoRef.current;
      const hadSpeech = segHasSpeechRef.current;
      const blob: Blob = await new Promise((resolve) => {
        rec.onstop = () => resolve(new Blob(segChunksRef.current, { type: rec.mimeType }));
        rec.stop();
      });
      if (hadSpeech && blob.size > 0) {
        pendingUploadsRef.current.push(
          uploadSegment(id, seq, startMs, eventId, photoId, blob, uploadType).then(() => refreshData()),
        );
      }
      if (!final) startSegment();
    };
    cutSegmentRef.current = cutSegment;

    // Détecte les pauses et coupe aux silences.
    const monitor = () => {
      const lvl = computeLevel(an, buf);
      const now = performance.now();
      if (lvl > SEG_SILENCE_LEVEL) {
        segHasSpeechRef.current = true;
        segLastSpeechRef.current = now;
      }
      const segAge = now - startTsRef.current - segStartMsRef.current;
      const pause = segHasSpeechRef.current && now - segLastSpeechRef.current > SEG_SILENCE_MS && segAge > SEG_MIN_MS;
      const tooLong = segHasSpeechRef.current && segAge > SEG_MAX_MS;
      if (!cuttingRef.current && (pause || tooLong)) {
        cuttingRef.current = true;
        void cutSegment(false).finally(() => {
          cuttingRef.current = false;
        });
      }
    };

    startSegment();
    setStatus("recording");
    setElapsedMs(0);
    timerRef.current = setInterval(() => setElapsedMs(performance.now() - startTsRef.current), 250);
    monitorRef.current = setInterval(monitor, 150);
    syncTimerRef.current = setInterval(() => void syncTimeline(id), 10000);
  }, [setStatus, syncTimeline, refreshData]);

  // Lance (ou relance) une capture de test sur le flux déjà ouvert.
  const runTest = useCallback(() => {
    const stream = streamRef.current;
    const an = analyserNodeRef.current;
    if (!stream || !an) return;
    setTestPhase("listening");
    setHeardSpeech(false);
    setMicHeard(null);

    const mimeType = pickMime();
    const testRec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const testChunks: Blob[] = [];
    testRec.ondataavailable = (e) => {
      if (e.data.size > 0) testChunks.push(e.data);
    };
    testRecorderRef.current = testRec;
    testRec.start();

    const finalizeTest = async () => {
      cancelAnimationFrame(detectRafRef.current);
      setTestPhase("checking");
      const blob: Blob = await new Promise((resolve) => {
        testRec.onstop = () => resolve(new Blob(testChunks, { type: testRec.mimeType }));
        testRec.stop();
      });
      try {
        const uploadType = (blob.type || "audio/webm").split(";")[0]!;
        const r = await fetch("/api/mic-check", {
          method: "POST",
          headers: { "content-type": uploadType },
          body: blob,
        });
        const { heard } = (await r.json()) as { heard: string };
        if (heard && heard.trim().length > 0) {
          setMicHeard(heard.trim());
          setTestPhase("ready");
        } else {
          setTestPhase("failed");
        }
      } catch {
        setTestPhase("failed");
      }
    };

    const buf = new Uint8Array(new ArrayBuffer(an.fftSize));
    const testStart = performance.now();
    let firstSpeech = 0;
    const loop = () => {
      if (statusRef.current !== "testing") return;
      const lvl = computeLevel(an, buf);
      const now = performance.now();
      if (lvl > SPEECH_THRESHOLD && firstSpeech === 0) {
        firstSpeech = now;
        setHeardSpeech(true); // feedback quasi-instantané "je t'entends"
      }
      if (firstSpeech !== 0 && now - firstSpeech > CAPTURE_AFTER_SPEECH_MS) {
        void finalizeTest();
        return;
      }
      if (firstSpeech === 0 && now - testStart > NO_SPEECH_TIMEOUT_MS) {
        cancelAnimationFrame(detectRafRef.current);
        try {
          testRec.stop();
        } catch {
          /* ignore */
        }
        setTestPhase("failed");
        return;
      }
      detectRafRef.current = requestAnimationFrame(loop);
    };
    loop();
  }, []);

  // Ouvre le micro (deviceId courant) puis lance le test.
  const start = useCallback(async () => {
    setError(null);
    try {
      const id = deviceIdRef.current;
      // `audio: true` applique les réglages par défaut du navigateur, tous conçus
      // pour la téléphonie et tous nuisibles ici :
      //  - autoGainControl pousse le niveau jusqu'à l'écrêtage. Mesuré sur une session
      //    Samsung S24 : 122 échantillons collés à 0 dB, parole distordue, et le modèle
      //    n'entend plus les phonèmes (« Ambatobe » transcrit « Bessombes »).
      //  - noiseSuppression déforme le spectre de la voix et dégrade la transcription.
      //  - echoCancellation isole le locuteur proche et atténue les autres : exactement
      //    l'inverse de ce qu'on veut pour une famille réunie autour d'un écran (§1).
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(id ? { deviceId: { exact: id } } : {}),
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
        },
      });
      streamRef.current = stream;
      void refreshDevices();

      const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx();
      await ctx.resume();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      src.connect(an);
      audioCtxRef.current = ctx;
      analyserNodeRef.current = an;
      setAnalyser(an);
      setStatus("testing");
      runTest();
    } catch (err) {
      setError(err instanceof Error ? err.message : "micro indisponible");
      setStatus("error");
    }
  }, [refreshDevices, setStatus, runTest]);

  // Change de micro depuis la popup : relance le test avec le nouveau périphérique.
  const changeMic = useCallback(
    async (id: string) => {
      setDeviceId(id || null);
      teardownStream();
      await start();
    },
    [setDeviceId, teardownStream, start],
  );

  // Bouton "Continuer" : démarre le vrai enregistrement (réutilise le flux).
  const continueRecording = useCallback(async () => {
    await beginRecording();
  }, [beginRecording]);

  const retryTest = useCallback(() => {
    runTest();
  }, [runTest]);

  const cancelTest = useCallback(() => {
    if (statusRef.current !== "testing") return;
    cancelAnimationFrame(detectRafRef.current);
    try {
      testRecorderRef.current?.stop();
    } catch {
      /* ignore */
    }
    teardownStream();
    setStatus("idle");
  }, [setStatus, teardownStream]);

  const stop = useCallback(async () => {
    const id = recordingIdRef.current;
    if (!id) return;
    if (timerRef.current) clearInterval(timerRef.current);
    if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    if (monitorRef.current) clearInterval(monitorRef.current);
    cuttingRef.current = true; // bloque le moniteur

    const durationMs = Math.round(performance.now() - startTsRef.current);
    setStatus("uploading");
    try {
      // Coupe et envoie le dernier segment (le flux est encore ouvert).
      await cutSegmentRef.current?.(true);
      teardownStream();
      await Promise.allSettled(pendingUploadsRef.current);
      await fetch(`/api/recordings/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ timeline: timelineRef.current, durationMs, final: true }),
      });
      setStatus("transcribing");
      const r = await fetch(`/api/recordings/${id}/finalize`, { method: "POST" });
      if (!r.ok) throw new Error("finalize");
      refreshData();
      setStatus("idle");
    } catch (err) {
      setError(err instanceof Error ? err.message : "échec de l'envoi");
      setStatus("error");
    }
  }, [setStatus, teardownStream, refreshData]);

  const value: RecordingContextValue = {
    status,
    dataVersion,
    refreshData,
    elapsedMs,
    devices,
    deviceId,
    setDeviceId,
    changeMic,
    analyser,
    micHeard,
    testPhase,
    heardSpeech,
    start,
    stop,
    continueRecording,
    retryTest,
    cancelTest,
    noteView,
    error,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRecording(): RecordingContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRecording must be used within RecordingProvider");
  return ctx;
}

function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** VU-mètre : barre animée reflétant le niveau du micro en direct. */
function VuMeter({ analyser, className }: { analyser: AnalyserNode | null; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [clipping, setClipping] = useState(false);
  useEffect(() => {
    if (!analyser) return;
    const buf = new Uint8Array(new ArrayBuffer(analyser.fftSize));
    let raf = 0;
    let hot = 0;
    const tick = () => {
      const lvl = computeLevel(analyser, buf);
      if (ref.current) ref.current.style.width = `${Math.min(100, Math.round(lvl * 500))}%`;
      // Quelques images de suite avant d'alerter : un claquement isolé ne compte pas.
      hot = computeClipping(analyser, buf) > 0.01 ? hot + 1 : 0;
      if (hot === 3) setClipping(true);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [analyser]);
  return (
    <>
      <div className={`vu ${clipping ? "vu--clip" : ""} ${className ?? ""}`} data-testid="vu-meter">
        <div className="vu__fill" ref={ref} />
      </div>
      {clipping && (
        <p className="vu__warn" data-testid="vu-clip">
          Le micro sature : éloigne un peu le téléphone, sinon les noms seront mal transcrits.
        </p>
      )}
    </>
  );
}

/** Contrôle d'enregistrement (barre du haut) + overlay de test micro. */
export function RecordControl() {
  const {
    status,
    elapsedMs,
    devices,
    deviceId,
    changeMic,
    analyser,
    micHeard,
    testPhase,
    heardSpeech,
    start,
    stop,
    continueRecording,
    retryTest,
    cancelTest,
    error,
  } = useRecording();

  const micPicker = devices.length > 1 && (
    <select
      className="mictest__device"
      value={deviceId ?? ""}
      onChange={(e) => void changeMic(e.target.value)}
      title="Micro"
    >
      <option value="">Micro par défaut</option>
      {devices.map((d) => (
        <option key={d.deviceId} value={d.deviceId}>
          {d.label || "Micro"}
        </option>
      ))}
    </select>
  );

  return (
    <>
      {status === "testing" &&
        createPortal(
          <div className="mictest" data-testid="mic-test" onClick={cancelTest}>
            <div className="mictest__card" onClick={(e) => e.stopPropagation()}>
              <h3 className="mictest__title">Test micro</h3>

              {testPhase === "ready" ? (
                <>
                  <p className="mictest__ok" data-testid="mic-ok">✓ C'est bon, je t'entends&nbsp;!</p>
                  {micHeard && <p className="mictest__heard">«&nbsp;{micHeard}&nbsp;»</p>}
                  <button
                    className="mictest__continue"
                    data-testid="mic-continue"
                    onClick={() => void continueRecording()}
                  >
                    Continuer
                  </button>
                  <button className="mictest__cancel" onClick={cancelTest}>Annuler</button>
                </>
              ) : testPhase === "failed" ? (
                <>
                  <p className="mictest__fail">
                    Je n'ai rien entendu. Vérifie le micro (MacBook ouvert, bon micro sélectionné).
                  </p>
                  <VuMeter analyser={analyser} className="vu--big" />
                  {micPicker}
                  <button className="mictest__continue" onClick={retryTest}>Réessayer</button>
                  <button className="mictest__cancel" onClick={cancelTest}>Annuler</button>
                </>
              ) : (
                <>
                  <p className="mictest__prompt">Dis&nbsp;: «&nbsp;Bonjour Diapo&nbsp;»</p>
                  <VuMeter analyser={analyser} className="vu--big" />
                  <p className={`mictest__hint ${heardSpeech ? "mictest__hint--live" : ""}`}>
                    {testPhase === "checking"
                      ? "Vérification…"
                      : heardSpeech
                        ? "Je t'entends…"
                        : "La barre doit bouger quand tu parles…"}
                  </p>
                  {micPicker}
                  <button className="mictest__cancel" onClick={cancelTest}>Annuler</button>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}

      {status === "recording" ? (
        <div className="rec rec--on" data-testid="rec-control">
          <span className="rec__dot" />
          <span className="rec__time">{fmt(elapsedMs)}</span>
          <VuMeter analyser={analyser} className="vu--mini" />
          <button className="rec__btn rec__btn--stop" onClick={() => void stop()} data-testid="rec-stop">
            Arrêter
          </button>
        </div>
      ) : (
        <div className="rec" data-testid="rec-control">
          <button
            className="rec__btn rec__btn--start"
            onClick={() => void start()}
            disabled={status === "uploading" || status === "transcribing" || status === "testing"}
            data-testid="rec-start"
          >
            {status === "uploading" ? (
              "Envoi…"
            ) : status === "transcribing" ? (
              "Transcription…"
            ) : status === "testing" ? (
              "Test…"
            ) : (
              <>
                <MicIcon className="rec__icon" /> Enregistrer
              </>
            )}
          </button>
          {error && <span className="rec__err" title={error}>⚠︎</span>}
        </div>
      )}
    </>
  );
}
